
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/auth";
import { debugLogger } from '@/utils/debug';
import { fetchCurrentUserProfile } from '@/services/profileService';
import { buildProfileCompletionRedirect, needsProfileCompletion } from '@/utils/auth/profileCompletion';
import { sanitizeRedirectPath } from '@/utils/auth/sanitizeRedirect';

export const AuthRedirectHandler = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAdmin, loading } = useAuth();
  
  useEffect(() => {
    let active = true;

    // Don't redirect while auth is still loading
    if (loading) return;
    
    // Only redirect if user is authenticated
    if (user) {
      const redirectParam = sanitizeRedirectPath(searchParams.get('redirect'));
      const handleRedirect = async () => {
        const profile = await fetchCurrentUserProfile(user.id).catch(() => null);

        if (!active) {
          return;
        }

        if (needsProfileCompletion(user, profile)) {
          debugLogger.log("Authenticated user needs profile completion before continuing");
          navigate(buildProfileCompletionRedirect(redirectParam));
          return;
        }
      
        // Check if this is a password reset flow by looking at URL hash and search params
        const hasHashTokens = window.location.hash.includes('access_token') && window.location.hash.includes('type=recovery');
        const hasRecoveryType = searchParams.get('type') === 'recovery';
        const hasResetMode = searchParams.get('mode') === 'resetPassword';
      
        // If this is a password reset, redirect to profile security settings
        if (hasHashTokens || hasRecoveryType || hasResetMode) {
          debugLogger.log("Password reset detected, redirecting to profile security settings");
          // Clean up the URL and redirect to security tab with passwordReset flag
          window.history.replaceState({}, document.title, window.location.pathname);
          navigate('/profile?passwordReset=true&tab=security');
          return;
        }
      
        // Only process other redirects if there's actually a redirect parameter
        if (redirectParam !== '/') {
          // Check if redirect is to admin page and user is admin
          if (redirectParam.startsWith('/admin') && !isAdmin) {
            debugLogger.log("User is not admin, redirecting to home instead of admin page");
            navigate('/');
            return;
          }
        
          debugLogger.log("Redirecting authenticated user to:", redirectParam);
          navigate(redirectParam);
        } else {
          // If no redirect param, redirect to home 
          debugLogger.log("No redirect parameter, going to homepage");
          navigate('/');
        }
      };

      void handleRedirect();
    }

    return () => {
      active = false;
    };
  }, [user, isAdmin, loading, searchParams, navigate]);

  return null;
};
